import { useState } from 'react';
import dayjs from 'dayjs';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { buttonVariants } from '../button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { ViewMode } from 'components/DeviceDetails/constants';
import { DayPicker } from './DayPicker';
import { WeekPicker } from './WeekPicker';
import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';

// Maps each view mode to the dayjs unit used for chevron navigation
const NAV_UNIT = {
    [ViewMode.DAY]: 'day',
    [ViewMode.WEEK]: 'week',
    [ViewMode.MONTH]: 'month',
    [ViewMode.YEAR]: 'year',
};

function getWeekRangeLabel(endDate) {
    const start = endDate.subtract(6, 'day');
    const end = endDate;
    const crossesYear = start.year() !== end.year();

    const fmt = (d, withYear) =>
        new Intl.DateTimeFormat(undefined, {
            day: 'numeric',
            month: 'short',
            ...(withYear ? { year: 'numeric' } : {}),
        }).format(d.toDate());

    if (crossesYear) {
        return `${fmt(start, true)} – ${fmt(end, true)}`;
    }
    return `${fmt(start, false)} – ${fmt(end, false)}`;
}

function getDisplayLabel(date, view) {
    switch (view) {
        case ViewMode.WEEK:
            return getWeekRangeLabel(date);
        case ViewMode.MONTH:
            return date.format('MMM YYYY');
        case ViewMode.YEAR:
            return date.format('YYYY');
        default:
            return date.format('MMM D, YYYY');
    }
}

/**
 * Drop-in replacement for the MUI DatePicker used in ChartHeader.
 * Chevron navigation is self-contained — ChartHeader does not need
 * prev/next logic.
 *
 * Props:
 *   selectedDate    {dayjs}     current date value
 *   setSelectedDate {Function}  state setter
 *   view            {string}    ViewMode constant ('day' | 'week' | 'month' | 'year')
 *   disableFuture   {boolean}   when true, prevents navigation/selection beyond today
 *   daysWithData {number[]}  day-of-month numbers (1–31) with available data;
 *                               renders an accent dot on those days in day view
 */
export function DatePicker({
    selectedDate,
    setSelectedDate,
    view,
    disableFuture = false,
    daysWithData = [],
    onViewMonthChange,
    monthsWithData = [],
    onViewYearChange,
    yearsWithData = [],
    onViewPageChange,
}) {
    const [open, setOpen] = useState(false);

    // Snapshot today once per render — passed as maxDate to all sub-pickers
    // so future days/months/years are disabled. When the accent color config
    // fix lands, bg-accent / text-accent-foreground here will automatically
    // pick up the correct token value.
    const maxDate = disableFuture ? dayjs() : null;
    const navUnit = NAV_UNIT[view] ?? 'day';

    const canNavigateForward = !maxDate || selectedDate.isBefore(maxDate, navUnit);

    const handleSelect = (date) => {
        setSelectedDate(date);
        setOpen(false);
    };

    const displayLabel = getDisplayLabel(selectedDate, view);

    // On open, move focus to the roving cell (the selected day/month/year)
    // instead of the popover container so arrow-key navigation works at once.
    const handleOpenAutoFocus = (e) => {
        const cell = e.currentTarget?.querySelector('[role="grid"] [tabindex="0"]');
        if (cell) {
            e.preventDefault();
            cell.focus();
        }
    };

    return (
        <div className="flex items-center gap-1">


            {/* Trigger */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        aria-label={`Change date, currently ${displayLabel}`}
                        className={cn(
                            buttonVariants({ variant: 'outline' }),
                            'h-9 gap-2 px-3 text-sm font-normal'
                        )}
                    >
                        <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
                        <span>{displayLabel}</span>
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" onOpenAutoFocus={handleOpenAutoFocus}>
                    {view === ViewMode.DAY && (
                        <DayPicker
                            selectedDate={selectedDate}
                            onSelect={handleSelect}
                            maxDate={maxDate}
                            daysWithData={daysWithData}
                            onViewMonthChange={onViewMonthChange}
                            monthsWithData={monthsWithData}
                            yearsWithData={yearsWithData}
                        />
                    )}
                    {view === ViewMode.WEEK && (
                        <WeekPicker
                            selectedDate={selectedDate}
                            onSelect={handleSelect}
                            maxDate={maxDate}
                            daysWithData={daysWithData}
                            onViewMonthChange={onViewMonthChange}
                            monthsWithData={monthsWithData}
                            yearsWithData={yearsWithData}
                        />
                    )}
                    {view === ViewMode.MONTH && (
                        <MonthPicker
                            selectedDate={selectedDate}
                            onSelect={handleSelect}
                            maxDate={maxDate}
                            monthsWithData={monthsWithData}
                            yearsWithData={yearsWithData}
                            onViewYearChange={onViewYearChange}
                        />
                    )}
                    {view === ViewMode.YEAR && (
                        <YearPicker
                            selectedDate={selectedDate}
                            onSelect={handleSelect}
                            maxDate={maxDate}
                            yearsWithData={yearsWithData}
                            onViewPageChange={onViewPageChange}
                        />
                    )}
                </PopoverContent>
            </Popover>
            {/* Prev chevron */}
            <button
                type="button"
                onClick={() => setSelectedDate((d) => d.subtract(1, navUnit))}
                aria-label={`Previous ${navUnit}`}
                className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-9 w-9 p-0'
                )}
            >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            {/* Next chevron */}
            <button
                type="button"
                onClick={() => setSelectedDate((d) => d.add(1, navUnit))}
                disabled={!canNavigateForward}
                aria-disabled={!canNavigateForward}
                aria-label={`Next ${navUnit}`}
                className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-9 w-9 p-0 disabled:pointer-events-none disabled:opacity-50'
                )}
            >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    );
}
